import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { referralApi } from '../api/referral';
import { useAuth } from '../contexts/AuthContext';

type TabKey = 'home' | 'buy' | 'network' | 'payouts' | 'account';

const AIML_PRESALE_USD = 0.012; // presale price — adjust or wire to contract later

const usd = (n?: number) => '$' + (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compact = (n?: number) => {
  const v = n ?? 0;
  if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return String(Math.round(v));
};
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const initials = (email?: string) => ((email || '?').split('@')[0].slice(0, 2) || '?').toUpperCase();

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-3xl ${className}`} style={{ backgroundColor: '#15122e', border: '1px solid rgba(255,255,255,0.06)' }}>{children}</div>;
}

function StatChips({ aimlEarned, personalSpent }: { aimlEarned?: number; personalSpent?: number }) {
  const compactNum = (n?: number) => {
    const v = n ?? 0;
    if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
    if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
    return String(Math.round(v));
  };
  const chips = [
    { sym: 'AIML', val: '$' + AIML_PRESALE_USD, sub: 'Presale price', color: '#34D399' },
    { sym: 'Total AIML', val: compactNum(aimlEarned), sub: 'Purchase + incentives', color: '#FBBF24' },
    { sym: 'Total spent', val: '$' + compactNum(personalSpent), sub: 'USDT', color: '#22D3EE' },
  ];
  return (
    <div className="flex gap-3 mb-5">
      {chips.map((c) => (
        <Card key={c.sym} className="flex-1 p-3">
          <div className="text-[11px] font-semibold truncate" style={{ color: '#c4b5fd' }}>{c.sym}</div>
          <div className="text-base font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif', color: c.color }}>{c.val}</div>
          <div className="text-[10px]" style={{ color: '#8B82A8' }}>{c.sub}</div>
        </Card>
      ))}
    </div>
  );
}

export default function Dashboard({ onNavigate }: { onNavigate: (t: TabKey) => void }) {
  const { user } = useAuth();
  const summary = useQuery({ queryKey: ['referral-me'], queryFn: referralApi.me });
  const referees = useQuery({ queryKey: ['referral-referees'], queryFn: referralApi.referees });
  const [copied, setCopied] = useState(false);

  if (summary.isLoading) return <p className="text-sm" style={{ color: '#8B82A8' }}>Loading your dashboard…</p>;
  if (summary.isError || !summary.data)
    return (
      <div>
        <p className="text-sm mb-3" style={{ color: '#F87171' }}>Couldn’t load your data.</p>
        <button onClick={() => summary.refetch()} className="rounded-xl px-4 py-2 text-sm" style={{ backgroundColor: '#1d1640', color: '#c4b5fd' }}>Try again</button>
      </div>
    );

  const d = summary.data;
  const personal = d.spend?.personalUsdt ?? 0;
  const remaining = d.tier?.needs?.personalSpendUsdt ?? 0;
  const target = personal + remaining;
  const pct = target > 0 ? Math.min(100, Math.round((personal / target) * 100)) : 100;
  const kycOk = d.kycStatus === 'APPROVED';
  const r = d.tier?.rates;
  const preview = (referees.data?.referees ?? []).slice(0, 3);

  async function copyLink() {
    if (!d.referralUrl) return;
    try { await navigator.clipboard.writeText(d.referralUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  }

  return (
    <div className="space-y-5 pb-2">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] tracking-widest" style={{ color: '#8B82A8' }}>AFFILIATE DASHBOARD</div>
          <h1 className="text-3xl font-bold leading-tight" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Welcome back</h1>
          <div className="text-sm" style={{ color: '#8B82A8' }}>{user?.email}</div>
        </div>
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-sm font-bold text-white" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)' }}>{initials(user?.email)}</div>
      </div>

      <StatChips aimlEarned={d.incentives?.aimlEarned} personalSpent={personal} />

      {/* Payable USDT */}
      <div className="rounded-3xl p-5" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)' }}>
        <div className="flex justify-between items-start">
          <div className="text-sm text-white/85">Payable USDT</div>
          <span className="text-[11px] px-2 py-1 rounded-full font-semibold" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
            {kycOk ? 'KYC ✓ Verified' : 'KYC needed'}
          </span>
        </div>
        <div className="text-4xl font-bold text-white mt-1" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{usd(d.earnings?.payableUsdt)}</div>
        <div className="text-xs mt-1 text-white/90">+{usd(d.earnings?.totalCommissionEarnedUsdt)} all-time · {usd(d.earnings?.totalCommissionPaidUsdt)} paid</div>
        <button onClick={() => onNavigate('payouts')} className="mt-4 w-full rounded-2xl py-3 text-sm font-semibold text-white" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
          Connect payout wallet
        </button>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Clicks', sub: 'Total link clicks', value: d.stats?.totalClicks ?? 0, color: '#22D3EE' },
          { label: 'Signups', sub: 'People who joined', value: d.stats?.totalSignups ?? 0, color: '#FBBF24' },
          { label: 'Qualifying', sub: 'Referrals who bought', value: d.stats?.qualifyingReferrals ?? 0, color: '#34D399' },
          { label: 'AIML earned', sub: 'Incentive tokens', value: compact(d.incentives?.aimlEarned), color: '#FBBF24' },
        ].map((s) => (
          <Card key={s.label} className="p-4">
            <div className="text-[10px] tracking-widest" style={{ color: '#8B82A8' }}>{s.label.toUpperCase()}</div>
            <div className="text-2xl font-bold mt-1" style={{ fontFamily: '"Space Grotesk", sans-serif', color: s.color }}>{s.value}</div>
            <div className="text-[11px] mt-1" style={{ color: '#8B82A8' }}>{s.sub}</div>
          </Card>
        ))}
      </div>

      {/* Commission structure */}
      <Card className="p-5">
        <div className="font-semibold mb-3" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Commission structure</div>
        <div className="flex gap-3">
          {[
            { v: `${r?.usdtPercent ?? 0}%`, t: 'USDT · L1', c: '#34D399' },
            { v: `${r?.aimlPercent ?? 0}%`, t: 'AIML · L1', c: '#FBBF24' },
            { v: `${r?.l2UsdtPercent ?? 0}%+${r?.l2AimlPercent ?? 0}%`, t: 'L2 indirect', c: '#a78bfa' },
          ].map((x) => (
            <div key={x.t} className="flex-1 rounded-2xl p-3 text-center" style={{ backgroundColor: '#1d1640' }}>
              <div className="text-lg font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif', color: x.c }}>{x.v}</div>
              <div className="text-[10px] mt-1" style={{ color: '#8B82A8' }}>{x.t}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Progress */}
      <Card className="p-5">
        <div className="flex justify-between items-center mb-3">
          <span className="font-semibold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{d.tier?.badge} {d.tier?.label}</span>
          {d.tier?.next && <span className="text-xs font-semibold" style={{ color: '#a78bfa' }}>{pct}%</span>}
        </div>
        {d.tier?.next ? (
          <>
            <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#2a2350' }}>
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#8B5CF6,#EC4899)' }} />
            </div>
            <div className="text-xs mt-2" style={{ color: '#8B82A8' }}>{usd(personal)} of {usd(target)} personal spend · Next: {d.tier.nextLabel}</div>
          </>
        ) : (
          <div className="text-xs" style={{ color: '#34D399' }}>You’ve reached the top tier 🎉</div>
        )}
      </Card>

      {/* Referral link */}
      <Card className="p-5">
        <div className="text-[11px] tracking-widest mb-2" style={{ color: '#8B82A8' }}>YOUR REFERRAL LINK</div>
        {d.referralUrl ? (
          <>
            <div className="text-sm mb-3 truncate" style={{ fontFamily: '"JetBrains Mono", monospace', color: '#c4b5fd' }}>{d.referralUrl}</div>
            <button onClick={copyLink} className="w-full rounded-2xl py-3 text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)' }}>{copied ? 'Copied' : 'Copy link'}</button>
          </>
        ) : (
          <div className="text-sm" style={{ color: '#8B82A8' }}>Reach Bronze (spend $100) to unlock your referral link.</div>
        )}
      </Card>

      {/* Direct referrals preview */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <span className="font-semibold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Your direct referrals</span>
          <button onClick={() => onNavigate('network')} className="text-xs font-semibold" style={{ color: '#a78bfa' }}>View all</button>
        </div>
        {preview.length === 0 ? (
          <Card className="p-5"><div className="text-sm" style={{ color: '#8B82A8' }}>No referrals yet. Share your link to get started.</div></Card>
        ) : (
          <div className="space-y-3">
            {preview.map((ref) => (
              <Card key={ref.id} className="p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: '#1d1640' }}>{initials(ref.email)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{ref.email}</div>
                  <div className="text-[11px]" style={{ color: '#8B82A8' }}>{fmtDate(ref.joinedAt)} · {ref.qualifyingPurchases} buy{ref.qualifyingPurchases === 1 ? '' : 's'}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold" style={{ color: '#34D399' }}>{usd(ref.youEarnedUsdt)}</div>
                  <div className="text-[10px]" style={{ color: '#8B82A8' }}>earned</div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}